'use client';

import { AIImageBadge } from '@/components/customFields/AIImageBadge';
import HeadlineField from '@/components/customFields/HeadlineField';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import { HeadlineTag } from '@/components/customFields/types';
import { generateAltText } from '@/components/customFields/utils';
import { isAIGeneratedAsset } from '@/helper/asset';
import { getLightboxImage } from '@/helper/image';
import type { ComponentGalleryStoryblok } from '@/types/component-types-sb';
import { ColumnWidth, type ImageWidthDynamic } from '@/types/layoutTypes';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import React from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';

import classes from './Gallery.module.scss';

const Gallery: React.FC<{ blok: ComponentGalleryStoryblok }> = ({ blok }) => {
  const aspectRatio = Number(blok.aspect_ratio);
  const columns = Number(blok.layout) as 3 | 4 | 6;
  let columnsDesktop: ColumnWidth;

  switch (blok.layout) {
    case '3':
      columnsDesktop = ColumnWidth.ONE_THIRD;
      break;
    case '4':
      columnsDesktop = ColumnWidth.ONE_QUARTER;
      break;
    case '6':
      columnsDesktop = ColumnWidth.ONE_SIXTH;
      break;
  }

  const layout: ImageWidthDynamic = {
    columnsMobile: ColumnWidth.HALF,
    columnsTablet: ColumnWidth.HALF,
    columnsDesktop: columnsDesktop,
  };

  const [index, setIndex] = React.useState(-1);

  const lightboxImages = blok.use_lightbox
    ? blok.images.map((image) => ({
        ...getLightboxImage(image),
        aiGenerated: isAIGeneratedAsset(image),
        alt: generateAltText(image),
      }))
    : [];

  return (
    <div id={blok.anchor || undefined}>
      {blok.headline && (
        <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
          {blok.headline}
        </HeadlineField>
      )}
      <div className={classNames(classes.gallery, classes['gallery--' + columns])} {...storyblokEditable(blok)}>
        {blok.images.map((image, index) => {
          return (
            <figure
              key={`${image.id}-${index}`}
              className={classNames('content-image', {
                'content-image--clickable': blok.use_lightbox,
              })}
              onClick={blok.use_lightbox ? () => setIndex(index) : undefined}
              onKeyDown={(e) => {
                if (blok.use_lightbox && (e.key === 'Enter' || e.key === ' ')) {
                  setIndex(index);
                }
              }}
              tabIndex={blok.use_lightbox ? 0 : -1}
            >
              <div className={classes.imageWrapper} data-testid="gallery-image-wrapper">
                <StoryblokImage image={image} width={layout} aspectRatio={aspectRatio} />
              </div>
              {image.name && !!blok.has_caption && <figcaption>{image.name}</figcaption>}
            </figure>
          );
        })}
        {blok.use_lightbox && (
          <Lightbox
            plugins={[Captions]}
            index={index}
            open={index >= 0}
            close={() => setIndex(-1)}
            slides={lightboxImages}
            carousel={{
              finite: lightboxImages.length <= 1,
              imageProps: { className: classes.lightboxImage },
            }}
            portal={{ container: { className: classes.lightboxPortal } }}
            render={{
              buttonPrev: lightboxImages.length <= 1 ? () => null : undefined,
              buttonNext: lightboxImages.length <= 1 ? () => null : undefined,
              slideContainer: ({ slide, children }) => (
                <div className={classes.lightboxImageWrapper}>
                  {children}
                  {'aiGenerated' in slide && slide.aiGenerated === true ? <AIImageBadge /> : null}
                </div>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Gallery;
